import { useEffect, useState } from "react";
import { Table, ConfigProvider, Button, Input, Card, Pagination, Spin } from "antd";
import { FileTextOutlined, SearchOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useResumeDatabase } from "../../app/hooks/useResumeDatabase";
import Debounce from "../../app/utils/debounce";
import { observer } from "mobx-react-lite";
import { PaginatedResult } from "../../app/utils/pagination";

const debouncer = new Debounce();

const ResumeList = () => {
  const navigate = useNavigate();
  const [paginatedResult, setPaginatedResult] = useState(new PaginatedResult());
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const pageSize = 10;

  const { getResumeDatabaseDepartment } = useResumeDatabase({ pageSize: pageSize });

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);

    debouncer.debounce({
      debounce: () => {
        setCurrentPage(1);
        setDebouncedSearch(value);
      },
      debounceTimeout: 800,
    });
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const result = await getResumeDatabaseDepartment(currentPage, pageSize, debouncedSearch);
      setPaginatedResult(result);
    } catch (error) {
      const errorMessage = error?.response?.data?.details || error?.response?.data?.title || error?.message || "Department not found.";
      console.log(errorMessage);
      setPaginatedResult(new PaginatedResult());
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, debouncedSearch]);

  const handleView = (record) => {
    console.log("Record:", record);
    navigate(`resume-database`, { state: { departmentId: record.departmentId, departmentName: record.departmentName } });
  };

  const columns = [
    {
      title: "NO. OF RESUMES",
      dataIndex: "count",
      key: "count",
      sorter: (a, b) => a.noOfResumes - b.noOfResumes,
      render: (text) => <span className="text-xl font-medium text-black pl-4">{text}</span>,
    },
    {
      title: "DEPARTMENT",
      dataIndex: "departmentName",
      key: "departmentName",
      align: "center",
      render: (text) => <span className="text-xl font-medium text-black">{text}</span>,
    },
    {
      title: <span>&nbsp;</span>,
      key: "action",
      align: "center",
      render: (_, record) => (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            type="primary"
            style={{
              backgroundColor: "#F97316",
              borderColor: "#F97316",
              color: "white",
              fontWeight: "600",
              fontSize: "18px",
              borderRadius: "9999px",
              height: "35px",
              padding: "0 25px",
              display: "flex",
              alignItems: "center",
            }}
            className="mx-auto"
            onClick={() => handleView(record)}
          >
            <FileTextOutlined style={{ fontSize: "18px" }} />
            VIEW
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Card className="rounded-2xl px-4 md:px-16 py-8 shadow-2xl w-full overflow-hidden">
      <div className="pb-6">
        <p className="cardDes font-bold text-xl text-center">Resume Database</p>
      </div>
      <Input
        size="large"
        placeholder="Search here"
        onChange={handleSearchChange}
        value={searchText}
        prefix={<SearchOutlined style={{ fontSize: "30px", color: "#666" }} />}
        style={{
          borderRadius: "10px",
          padding: "10px 10px",
          fontSize: "20px",
          border: "1px solid #d9d9d9",
          maxWidth: "400px",
        }}
      />

      <ConfigProvider
        theme={{
          token: {
            colorBgContainer: "#E9E9E9",
            boxShadowSecondary: "0 4px 8px 0 rgba(0, 0, 0.5, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)",
            borderRadius: 8,
          },
          components: {
            Table: {
              headerBg: "#364042",
              headerColor: "white",
              headerSplitColor: "#364042",
            },
          },
        }}
      >
        {isLoading ? (
          <div className="col-span-3 flex justify-center py-20">
            <Spin tip="Fetching Job Openings..." />
          </div>
        ) : (
          <Table rowKey="id" columns={columns} dataSource={paginatedResult.data} className="recruitment-table" pagination={false} />
        )}

        <div className="flex justify-center mt-8">
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            className="job-search-pagination"
            total={paginatedResult.totalItems}
            onChange={(page) => {
              setCurrentPage(page);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            showSizeChanger={false}
          />
        </div>
      </ConfigProvider>
    </Card>
  );
};

export default observer(ResumeList);
